import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreatePlanAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, Input } from '@/presentation/components/common';
import type { PlanFormData } from '@/domain/repositories/IPlanRepository';
import type { ProductTier } from '@/domain/types/planCatalog';
import { TIER_DESCRIPTIONS, TIER_LABELS } from '@/domain/types/planCatalog';
import { httpClient } from '@/infrastructure/http/client/httpClient';

interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [productTier, setProductTier] = useState<ProductTier>('BASE');
  const [formData, setFormData] = useState<PlanFormData>({
    nombre: '',
    consultorios: 1,
    profesionales: 1,
    precio: 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      showError(t('pages.plans.validation.nameRequired'));
      return;
    }
    if (formData.nombre.length > 30) {
      showError(t('pages.plans.validation.nameMaxLength'));
      return;
    }
    if (!formData.consultorios || formData.consultorios < 1) {
      showError(t('pages.plans.validation.consultoriosRequired'));
      return;
    }
    if (!formData.profesionales || formData.profesionales < 1) {
      showError(t('pages.plans.validation.profesionalesRequired'));
      return;
    }
    if (!formData.precio || formData.precio < 0.01) {
      showError(t('pages.plans.validation.priceRequired'));
      return;
    }

    setIsLoading(true);
    try {
      // Crear con productTier vía API directa (el action legacy no lo envía).
      await httpClient.post('/plans', {
        ...formData,
        productTier,
      });
      success(t('pages.plans.createdSuccess'));
      onSuccess();
      setFormData({ nombre: '', consultorios: 1, profesionales: 1, precio: 0 });
      setProductTier('BASE');
    } catch (err) {
      // Fallback al action si el body extendido fallara por tipado.
      try {
        const action = container.get<CreatePlanAction>(TYPES.CreatePlanAction);
        await action.execute(formData);
        success(t('pages.plans.createdSuccess'));
        onSuccess();
      } catch (inner) {
        showError(inner instanceof Error ? inner.message : (err instanceof Error ? err.message : 'Error al crear plan'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500/70" onClick={onClose} />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="px-5 pt-5 pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('pages.plans.create')}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <Input
                  label={t('pages.plans.name')}
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder={t('pages.plans.namePlaceholder')}
                  maxLength={30}
                  required
                />

                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nivel de producto
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {(['BASE', 'AVANZADO'] as ProductTier[]).map((tier) => (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => setProductTier(tier)}
                        className={`text-left rounded-lg border px-3 py-2 ${
                          productTier === tier
                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/40'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {TIER_LABELS[tier]}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {TIER_DESCRIPTIONS[tier]}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input
                    label={t('pages.plans.consultorios')}
                    type="number"
                    min="1"
                    value={formData.consultorios}
                    onChange={(e) =>
                      setFormData({ ...formData, consultorios: parseInt(e.target.value, 10) || 1 })
                    }
                    required
                  />
                  <Input
                    label={t('pages.plans.profesionales')}
                    type="number"
                    min="1"
                    value={formData.profesionales}
                    onChange={(e) =>
                      setFormData({ ...formData, profesionales: parseInt(e.target.value, 10) || 1 })
                    }
                    required
                  />
                  <Input
                    label={t('pages.plans.price')}
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.precio}
                    onChange={(e) =>
                      setFormData({ ...formData, precio: parseFloat(e.target.value) || 0 })
                    }
                    required
                  />
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-gray-900/50 px-5 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" isLoading={isLoading}>
                {t('common.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
